import { openDB } from "idb";
import type { DomainMetadataDerivedFromUrl, HasTab, NetworkEvent } from "../../lib/Events";
import { type DbRecording, type DbRecordedEvent, type DbVideoFragment, type DbMhtmlCapture, NameOfIndexForRecordingUuidThenOriginRegisteredDomainThenDestinationRegisteredDomain, DbExtensionSettings, NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex, NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName, NameOfIndexForRecordingUuidThenTabIdThenTabUrl, NameOfIndexForRecordingUuidThenCategoryThenName, NameOfIndexForRecordingUuidThenCategoryThenEventIndex, NameOfIndexForEventListenerUuidThenEventName, NameOfIndexForReceivedEventUuidThenEventName, NameOfIndexForRecordingUuidThenNumericIndex } from "./DatabaseTypes";
import type { EvidenceDatabase, EvidenceDatabaseSchema } from "./EvidenceDbStruct";
import { ProcessInfo } from "../ProcessInfo";
import { InstrumentationEventDomEventReceived } from "../../lib/Events/InstrumentationEvent";

const createDatabase = async () => {
	try {
		ProcessInfo.log("Attempting to open database");
		const db = await openDB<EvidenceDatabaseSchema>("Evidence", 1, {
			upgrade(db) {
				const recordingsStore = db.createObjectStore("recordings", {
					keyPath: "recordingUuid" satisfies keyof DbRecording,
					autoIncrement: true,
				});
				const settingsStore = db.createObjectStore("extensionSettings", {
					keyPath: "settingsIndex" satisfies keyof DbExtensionSettings,
					autoIncrement: true,
				});
				const eventsStore = db.createObjectStore("events", {
					keyPath: "eventIndex" satisfies (keyof DbRecordedEvent),
					autoIncrement: true,
				});
				eventsStore.createIndex(NameOfIndexForRecordingUuidThenNumericIndex,
					["recordingUuid", "eventIndex"] satisfies (keyof DbRecordedEvent)[]);
				eventsStore.createIndex(NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName,
					["recordingUuid", "raw.requestId", "category", "eventName"] satisfies [keyof DbRecordedEvent, `${keyof NetworkEvent}.${NetworkEvent["raw"]["requestId"]}`, keyof DbRecordedEvent, keyof DbRecordedEvent]);
				eventsStore.createIndex(NameOfIndexForRecordingUuidThenOriginRegisteredDomainThenDestinationRegisteredDomain,
					["recordingUuid", "tab.registeredDomain", "destination.registeredDomain"] satisfies [
						keyof DbRecordedEvent, `${keyof HasTab}.${keyof DomainMetadataDerivedFromUrl}`, `${keyof NetworkEvent}.${keyof DomainMetadataDerivedFromUrl}`]);
				eventsStore.createIndex(NameOfIndexForRecordingUuidThenCategoryThenEventIndex,
						["recordingUuid", "category", "eventIndex"] satisfies [keyof DbRecordedEvent, keyof DbRecordedEvent, keyof DbRecordedEvent]);
				eventsStore.createIndex(NameOfIndexForRecordingUuidThenCategoryThenName,
						["recordingUuid", "category", "eventName"] satisfies [keyof DbRecordedEvent, keyof DbRecordedEvent, keyof DbRecordedEvent]);
				eventsStore.createIndex(NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex,
					["recordingUuid", "category", "eventName", "eventIndex"] satisfies [keyof DbRecordedEvent, keyof DbRecordedEvent, keyof DbRecordedEvent, keyof DbRecordedEvent]);
				eventsStore.createIndex(NameOfIndexForRecordingUuidThenTabIdThenTabUrl,
					["recordingUuid", "tab.tabId", "tab.url"] satisfies [
						keyof DbRecordedEvent,
						`${keyof DbRecordedEvent}.${keyof DbRecordedEvent["tab"]}`,
						`${keyof DbRecordedEvent}.${keyof DbRecordedEvent["tab"]}`
					]);
				eventsStore.createIndex(NameOfIndexForEventListenerUuidThenEventName,
					["raw.eventListenerUuid", "eventName"] satisfies [`${keyof InstrumentationEventDomEventReceived}.${keyof InstrumentationEventDomEventReceived["raw"]}`, keyof DbRecordedEvent],
					// {unique: true}
				)
				eventsStore.createIndex(NameOfIndexForReceivedEventUuidThenEventName,
					["raw.receivedEventUuid", "eventName"] satisfies [`${keyof InstrumentationEventDomEventReceived}.${keyof InstrumentationEventDomEventReceived["raw"]}`, keyof DbRecordedEvent],
					// {unique: true}
				)
				
				const videoStore = db.createObjectStore("videoFragments", {
					keyPath: "videoFragmentIndex" satisfies (keyof DbVideoFragment),
					autoIncrement: true,
				});
				videoStore.createIndex(NameOfIndexForRecordingUuidThenNumericIndex,
					["recordingUuid", "videoFragmentIndex"] satisfies (keyof DbVideoFragment)[]);
				const mhtmlRecordsStore = db.createObjectStore("mhtmlCaptures", {
					keyPath: "mhtmlCaptureIndex" satisfies keyof DbMhtmlCapture,
					autoIncrement: true,
				});
				mhtmlRecordsStore.createIndex(NameOfIndexForRecordingUuidThenNumericIndex,
					["recordingUuid", "mhtmlCaptureIndex"] satisfies (keyof DbMhtmlCapture)[]);
			}
		});
		ProcessInfo.log("Opened database");
		return db;
	} catch (e) {
		ProcessInfo.errorLog("Failed to open database", e);
		throw e;
	}
};

let evidenceDatabasePromise: Promise<EvidenceDatabase> | undefined;
export const requireEvidenceDatabase = async () => {
	return await (evidenceDatabasePromise ?? (evidenceDatabasePromise = createDatabase()));
}

export const getDatabase = async () => {
	try {
		return await requireEvidenceDatabase();
	} catch (e) {
		ProcessInfo.errorLog("Failed to create evidence database", e);
		return undefined;
	}
};
